import axios from "axios";
import pica from "pica";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { H5 } from "../../../../AbstractElements";
import useIsMobile from "../../../../Hooks/useIsMobile";
import { useAuth } from "../../../../_helper/Auth/AuthProvider";
import { useFileUpload } from "../../../../_helper/FileUpload/FileUploadProvider";
import DeleteFileModal from "../../../Modals/common/DeleteFileModal";
import FilePreviewModal from "../../../Modals/common/FilePreviewModal";
import FileUploadInput from "../../Concern/FileUploadInput";
import FileUploadModal from "../../Concern/FileUploadModal";
import FileCard from "./FileCard";

const Files = ({ data }) => {
  const isMobile = useIsMobile();
  const { theme, user, authToken } = useAuth();
  const { isFetchingFiles, files, setFiles, getAllFiles } = useFileUpload();
  const [fileInfo, setfileInfo] = useState([]);

  //for api connection
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const backendPort = process.env.REACT_APP_BACKEND_PORT;
  let apiUrl = `${backendUrl}`;
  if (backendPort) {
    apiUrl += `:${backendPort}`;
  }

  const workspace = "INFLOW";
  const ticketId = data?._id;

  //for file uploading state
  const [selectedFiles, setSelectedFiles] = useState([]); // updated to array
  //for file deletion state
  const [selectedFile, setSelectedFile] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({}); // progress per file
  const [fileUploadMeta, setFileUploadMeta] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [deletingFile, setDeletingFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadErrors, setUploadErrors] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAddFiles, setIsAddFiles] = useState(false);

  const [isOverwriteShown, setIsOverwriteShown] = useState(true);
  const [loadingOverwrite, setLoadingOverwrite] = useState(false);

  //for deleting modal alert
  const [isDeleteFile, setIsDeleteFile] = useState(false);

  //for downloading
  const [downloadProgressMap, setDownloadProgressMap] = useState({});
  const [expandedFiles, setExpandedFiles] = useState({});

  useEffect(() => {
    // get-all-files already returns full metadata for every file via fromFileMetadata,
    // so no additional fileinfo-get calls are needed.
    // Wrap each file in a single-element array so the UI can keep using fileInfo[0].
    setfileInfo(files.map((file) => ({ ...file, fileInfo: [file] })));
  }, [files]);

  const handleRemoveFile = (event, indexToRemove) => {
    event.stopPropagation(); // Prevents bubbling issues
    event.preventDefault(); // Prevents form submits (if inside a form)

    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );

    // Optionally remove related upload progress/errors/meta
    const removedFile = selectedFiles[indexToRemove];
    if (removedFile) {
      setUploadProgress((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });

      setUploadErrors((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });

      setFileUploadMeta((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Update file input change handler
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    // Filter out duplicates (optional enhancement)
    const existingFileNames = selectedFiles.map((f) => f.name);
    const uniqueNewFiles = newFiles.filter(
      (file) => !existingFileNames.includes(file.name)
    );

    const updatedFiles = [...selectedFiles, ...uniqueNewFiles];

    setSelectedFiles(updatedFiles);

    // Initialize progress and errors for newly added files
    const newProgress = { ...uploadProgress };
    const newErrors = { ...uploadErrors };
    uniqueNewFiles.forEach((file) => {
      newProgress[file.name] = 0;
      newErrors[file.name] = null;
    });

    setUploadProgress(newProgress);
    setUploadErrors(newErrors);
  };

  const fileNames = selectedFiles.map((file) => ({ name: file.name }));
  // Create a Set of existing file names for quick lookup
  const existingFileNames = new Set(files.map((file) => file.name));
  // Merge and add isFileExist property
  const combinedFiles = fileNames.map((file) => ({
    ...file,
    isFileExist: existingFileNames.has(file.name),
  }));

  const handleUploadFile = async (formDataToSubmit) => {
    if (!selectedFiles.length) {
      alert("Please select at least one file.");
      return;
    }

    if (!workspace || workspace.trim() === "") {
      alert("Workspace name is required.");
      return;
    }

    if (!authToken) {
      alert("Authentication token is missing. Please log in again.");
      return;
    }

    setIsUploadModalOpen(true);
    setIsUploading(true);
    setUploadStatus("Preparing upload...");
    setUploadProgress({});
    setUploadErrors({});
    setFileUploadMeta({});

    try {
      const newUploadErrors = {};

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        try {
            // Resize image if needed (max 800x800 px)
            let resizedFile = file;
            if (file.type.startsWith("image/")) {
              const image = new Image();
              image.src = URL.createObjectURL(file);
              await new Promise((resolve) => {
                image.onload = resolve;
              });

              const canvas = document.createElement("canvas");
              const maxWidth = 800;
              const maxHeight = 800;

              let width = image.width;
              let height = image.height;

              if (width > maxWidth || height > maxHeight) {
                const scale = Math.min(maxWidth / width, maxHeight / height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
              }

              canvas.width = width;
              canvas.height = height;

              const ctx = canvas.getContext("2d");
              ctx.drawImage(image, 0, 0, width, height);

              const resizedBlob = await pica().toBlob(canvas, {
                quality: 3,
                unsharpAmount: 80,
              });
              resizedFile = new File([resizedBlob], file.name, {
                type: file.type,
              });
            }

            setUploadStatus(`Uploading ${file.name}...`);
            const formData = new FormData();
            formData.append("file", resizedFile, file.name);
            formData.append("uuid", ticketId);
            formData.append("workspace", workspace);

            const startTime = Date.now();

          await axios.post(`${apiUrl}/api/file-upload`, formData, {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percentCompleted = Math.round((loaded * 100) / total);
              const elapsedTime = (Date.now() - startTime) / 1000;
              const speed = loaded / elapsedTime;
              const timeRemainingSec =
                speed > 0 ? Math.round((total - loaded) / speed) : null;

              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: percentCompleted,
              }));

              setFileUploadMeta((prev) => ({
                ...prev,
                [file.name]: {
                  timeRemainingSec,
                  speed,
                },
              }));
            },
          });

          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: 100,
          }));

          setFileUploadMeta((prev) => ({
            ...prev,
            [file.name]: {
              ...prev[file.name],
              timeRemainingSec: 0,
            },
          }));
        } catch (err) {
          console.error(`❌ Failed to process ${file.name}`, err);

          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: -1,
          }));

          const errorMsg =
            err.response?.data?.detail ||
            err.response?.data?.error ||
            err.message ||
            "Upload failed";
          newUploadErrors[file.name] = errorMsg;

          setFileUploadMeta((prev) => ({
            ...prev,
            [file.name]: {
              ...prev[file.name],
              timeRemainingSec: null,
            },
          }));
        }
      }

      setIsUploading(false);
      setUploadErrors(newUploadErrors);

      const existingFiles = Object.keys(newUploadErrors).filter((fileName) =>
        combinedFiles.some((f) => f.name === fileName && f.isFileExist)
      );

      const failedUploads = Object.keys(newUploadErrors).filter(
        (fileName) =>
          !combinedFiles.some((f) => f.name === fileName && f.isFileExist)
      );

      if (Object.keys(newUploadErrors).length === 0) {
        toast.success("All files uploaded successfully!", {
          autoClose: 1500,
        });
        setSelectedFiles([]);
        getAllFiles({
          uuid: ticketId,
          workspace: "INFLOW",
        });
        setTimeout(() => {
          setIsUploadModalOpen(false);
        }, 1500);
      } else {
        let errorMessage = "";

        if (failedUploads.length > 0) {
          errorMessage += `Failed to upload: ${failedUploads.join(", ")}.\n`;
        }

        if (existingFiles.length > 0) {
          errorMessage += `These files already exist: ${existingFiles.join(
            ", "
          )}.`;
        }

        toast.error(errorMessage, {
          autoClose: 2500,
        });

        setUploadStatus("Some files had issues.");
      }
    } catch (metaApiError) {
      console.error("❌ Upload process failed:", metaApiError);
      setIsUploading(false);
      setUploadStatus("Upload failed during processing stage.");
      toast.error("Upload failed during file processing.", {
        autoClose: 2000,
      });
    }
  };

  const [downloadInfo, setDownloadInfo] = useState({
    progress: 0,
    completed: false,
    total: 0,
    loaded: 0,
  });

  const handleDownloadFiles = async (fileInfo, files, workspaceName) => {
    for (const file of files) {
      // Remove file extension from file.path
      //const fileId = file.path.replace(/\.[^/.]+$/, ""); // Removes file extension
      const fileId = file.name.includes("camera_")
        ? file.name.replace(/\.[^/.]+$/, "")
        : file.path.replace(/\.[^/.]+$/, "");

      // Initialize progress for this file and show its progress bar
      setDownloadProgressMap((prev) => ({
        ...prev,
        [fileId]: 0,
      }));

      setExpandedFiles((prev) => ({
        ...prev,
        [fileId]: true,
      }));

      // Call handleDownloadFile without the file extension in file.path
      await handleDownloadFile(
        fileInfo?.fileInfo[0],
        file.name,
        fileId,
        workspaceName
      );

      // Keep progress bar visible for a moment after completion
      setTimeout(() => {
        setExpandedFiles((prev) => ({
          ...prev,
          [fileId]: false,
        }));
      }, 2000);

      await new Promise((resolve) => setTimeout(resolve, 1000)); // 1-sec delay to prevent browser blocking
    }
  };

  const handleDownloadFile = async (
    fileInfo,
    fileName,
    fileWithOptionalPath,
    workspaceName
  ) => {
    setDownloadingFile(fileWithOptionalPath);

    try {
      setIsDownloading(true);
      if (!authToken) {
        toast.error("Authentication token is missing.");
        return;
      }

      const response = await axios({
        method: "post",
        url: `${apiUrl}/api/file-download`,
        data: {
          token: authToken,
          fileWithOptionalPath,
          workspaceName,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          // Prevent division by 0 or invalid total values
          const progress =
            fileInfo?.fileSize && fileInfo?.fileSize > 0
              ? Math.floor((loaded * 100) / fileInfo.fileSize)
              : 0;

          // Update downloadInfo state to track the progress of the download
          setDownloadInfo({
            progress,
            loaded,
            total: fileInfo?.fileSize || 0,
            completed: progress === 100,
          });

          // Also update the progress for this specific file
          setDownloadProgressMap((prev) => ({
            ...prev,
            [fileWithOptionalPath]: progress,
          }));
        },
      });

      if (response.status === 200) {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`Downloaded: ${fileName}`);

        // Set progress to 100% when complete
        setDownloadProgressMap((prev) => ({
          ...prev,
          [fileWithOptionalPath]: 100,
        }));

        // Update downloadInfo to mark as completed
        setDownloadInfo((info) => ({
          ...info,
          completed: true,
        }));

        // Optional: remove file after a short delay
        setTimeout(() => {
          setDownloadInfo({
            progress: 0,
            completed: false,
            total: 0,
            loaded: 0,
          });
        }, 2000);
      } else {
        toast.error(`Failed to download: ${fileName}`);
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Error downloading ${fileName}: ${error.message}`);
    } finally {
      setIsDownloading(false);
      setDownloadingFile(null);
    }
  };

  const formatBytes = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

  const handleDeleteFile = async (file, type) => {
    const fileInfoData = {
      comments: file?.fileInfo[0]?.comments,
      creatDate: file?.fileInfo[0]?.creatDate,
      creator: file?.fileInfo[0]?.creator,
      extension: file?.fileInfo[0]?.extension, // default extension
      filename: file?.fileInfo[0]?.filename,
      fileSize: file?.fileInfo[0]?.fileSize, // default file size
      id: file?.fileInfo[0]?.id, // fallback id
      key: file?.fileInfo[0]?.key,
      serverpath: "",
      status: 0, // status can be static or dynamic based on your needs
      uuid: ticketId, // assuming `ticketId` is set correctly
      workspace: "INFLOW",
    };

    setDeletingFile(
      file.name.includes("camera_")
        ? `${file.name.replace(/\.[^/.]+$/, "")}`
        : `${file.key}`
    );
    setLoadingOverwrite(true); // Show loading state

    const getKeyByName = (fileName) => {
      const file = files.find((f) => f.name === fileName);
      return file ? file.key : null;
    };

    const trimmedKey = getKeyByName(
      `${ticketId}_${file.name.replace(/\s+/g, "_")}`
    );
    const key = type === "delete" ? file.key : trimmedKey;
    const path =
      type === "delete"
        ? file.name
        : `${ticketId}_${file.name.replace(/\s+/g, "_")}`;

    try {
      if (!authToken) throw new Error("No authentication token found.");

      //First API call: delete file
      const deleteResponse = await axios.post(`${apiUrl}/api/delete-file`, {
        token: authToken,
        params: {
          workspace: "INFLOW",
          path: file.name.includes("camera_")
            ? `${file.name.replace(/\.[^/.]+$/, "")}`
            : `${file.key}`,
          //path: `${file.key}`,
        },
      });

      if (deleteResponse.status !== 200) {
        throw new Error("Failed to delete file.");
      }

      // Second API call: remove from date-imap
      const imapResponse = await axios.post(`${apiUrl}/api/date-imap-remove`, {
        token: authToken,
        params: {
          key: key,
          uuid: ticketId,
          workspace: "INFLOW",
        },
      });

      if (imapResponse.status !== 200) {
        throw new Error("Failed to remove file from date-imap.");
      }

      // Second API call: remove from date-imap
      const deleteInfoResponse = await axios.post(
        `${apiUrl}/api/fileinfo-remove`,
        {
          token: authToken,
          data: fileInfoData, // Only send the `data` part here
        }
      );

      if (deleteInfoResponse.status !== 200) {
        throw new Error("Failed to remove file info.");
      }

      // Refresh file list only after both calls succeed
      //await getAllFiles({ uuid: ticketId, workspace: "INFLOW" });
      // Remove the deleted file from the state
      setFiles((prevFiles) => prevFiles.filter((f) => f.key !== file.key));

      // Reset UI state
      setUploadStatus(""); // Optional reset
      setIsOverwriteShown(false); // Hide error & button
      toast.success("File deleted successfully!", {
        autoClose: 2000,
      });
    } catch (err) {
      console.error("File deletion error:", err.response?.data || err.message);
      toast.error(err.message || "Something went wrong.", { autoClose: 1000 });
    } finally {
      setLoadingOverwrite(false); // Ensure loading state is removed
      setDeletingFile(null); // Clear deleting file state
    }
  };

  const toggleModal = () => setIsDeleteFile(!isDeleteFile);

  const handleDeleteClick = async (file) => {
    try {
      setSelectedFile(file); // Save the file to the state
      setIsDeleteFile(true);
    } catch (err) {
      console.error("Error while preparing file for deletion:", err);
    }
  };

  //open upload modal if there is selected files
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setIsUploadModalOpen(true);
    }
  }, [selectedFiles]);

  //for attachment previewing
  const [downloadingFiles, setDownloadingFiles] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileExtension, setFileExtension] = useState(""); // Pass the correct extension here
  const [currentFileId, setCurrentFileId] = useState(null);

  const handlePreviewFile = async (fileId, fileInfo, workspaceName) => {
    try {
      setDownloadingFiles(fileId);

      const response = await axios.get(`${apiUrl}/api/file-get`, {
        headers: {
          token: authToken,
        },
        params: {
          key: fileId,
          workspaceName: workspaceName,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const blobUrl = URL.createObjectURL(blob);
      const contentDisposition = response.headers["content-disposition"];
      const fileExtension = fileInfo.extension || "";
      const filename =
        extractFilename(contentDisposition) || `file.${fileExtension}`;

      const isInline =
        contentDisposition?.toLowerCase().includes("inline") ||
        isPreviewable(response.headers["content-type"]);

      const isVideo = response.headers["content-type"].startsWith("video/");
      const isPdf = response.headers["content-type"] === "application/pdf";

      if (isPdf) {
        // Open PDF in a new tab
        window.open(blobUrl, "_blank");
      } else if (isVideo) {
        // Check if the video format is supported by the browser
        const videoElement = document.createElement("video");
        const canPlay = videoElement.canPlayType(
          response.headers["content-type"]
        );

        if (canPlay === "" || canPlay === "no") {
          // Video format not supported
          alert(
            `This video format (${response.headers["content-type"]}) is not supported by your browser.`
          );

          // Offer to download instead
          // if (confirm('Would you like to download the file instead?')) {
          //   const a = document.createElement('a')
          //   a.href = blobUrl
          //   a.download = filename
          //   document.body.appendChild(a)
          //   a.click()
          //   document.body.removeChild(a)
          // }
        } else {
          // Video is supported, show in modal
          openModal(blobUrl, fileExtension);
        }
      } else if (isInline) {
        openModal(blobUrl, fileExtension); // Your modal viewer for previewing
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error("❌ Error fetching file:", error);
    } finally {
      setDownloadingFiles(null);
    }
  };

  const extractFilename = (contentDisposition) => {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition || ""
    );
    return match ? match[1].replace(/['"]/g, "") : null;
  };

  const isPreviewable = (contentType = "") => {
    const previewableTypes = [
      "image/",
      "video/",
      "audio/",
      "application/pdf",
      "text/",
    ];
    return previewableTypes.some((type) => contentType.startsWith(type));
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = (fileUrl, extension) => {
    setPreviewImageUrl(fileUrl);
    setFileExtension(extension); // Set the file extension for media handling
    setIsModalOpen(true); // Open the modal
  };

  return (
    <Row style={{ marginTop: 30 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <H5>Files</H5>
        {files.length > 0 && (
          <label
            style={{
              background: "#16c7f9",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: "14px",
              display: "inline-block",
            }}
            onMouseOver={(e) => (e.target.style.background = "#0056b3")}
            //onMouseOut={(e) => (e.target.style.background = "#16c7f9")}
            onClick={() => setIsAddFiles(!isAddFiles)}
          >
            <i
              className={`${isAddFiles ? "fa fa-close" : "fa fa-plus"}`}
              style={{ fontSize: 14, paddingRight: 8 }}
            ></i>
            {isAddFiles ? "Cancel" : "Add Files"}

            {!isMobile && (
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            )}
          </label>
        )}
      </div>

      <div
        style={{
          transition: "opacity 0.3s ease-in",
          opacity: isAddFiles ? 1 : 0,
          maxHeight: isAddFiles ? "500px" : "0px",
          overflow: "hidden",
        }}
      >
        {isAddFiles && (
          <FileUploadInput
            theme={theme}
            selectedFiles={selectedFiles}
            handleFileChange={handleFileChange}
          />
        )}
      </div>

      {isFetchingFiles ? (
        <p className="text-center">Loading files...</p>
      ) : (
        <Col md="12" style={{ marginBottom: 20 }}>
          <div style={{ marginTop: "15px" }}>
            {fileInfo.map((file, index) => {
              const file_extension = file.name.split(".").pop();
              const fileId = file.name.includes("camera_")
                ? `${file.name.replace(/\.[^/.]+$/, "")}`
                : `${file.key}`;
              const showProgress =
                expandedFiles[fileId] || downloadingFile === fileId;
              const progress = downloadProgressMap[fileId] || 0;

              return (
                <FileCard
                  file={file}
                  workspace={workspace}
                  fileId={fileId}
                  theme={theme}
                  downloadingFile={downloadingFile}
                  downloadingFiles={downloadingFiles}
                  isMobile={isMobile}
                  handlePreviewFile={handlePreviewFile}
                  handleDownloadFiles={handleDownloadFiles}
                  handleDeleteClick={handleDeleteClick}
                  loadingOverwrite={loadingOverwrite}
                  deletingFile={deletingFile}
                  progress={progress}
                  showProgress={showProgress}
                  downloadInfo={downloadInfo}
                  formatBytes={formatBytes}
                />
              );
            })}
          </div>

          <div style={{ maxWidth: "100%" }}>
            <FileUploadModal
              isOpen={isUploadModalOpen}
              uploadProgress={uploadProgress}
              selectedFiles={selectedFiles}
              uploadStatus={uploadStatus}
              setUploadStatus={setUploadStatus}
              setIsUploadModalOpen={setIsUploadModalOpen}
              ticketId={ticketId}
              files={files}
              uploadErrors={uploadErrors}
              fileUploadMeta={fileUploadMeta}
              handleRemoveFile={handleRemoveFile}
              handleDeleteFile={handleDeleteFile}
              isOverwriteShown={isOverwriteShown}
              loadingOverwrite={loadingOverwrite}
              setSelectedFiles={setSelectedFiles}
              handleUploadFile={handleUploadFile}
              isUploading={isUploading}
            />

            {files.length === 0 && (
              <FileUploadInput
                theme={theme}
                selectedFiles={selectedFiles}
                handleFileChange={handleFileChange}
              />
            )}
          </div>
        </Col>
      )}

      {/* Modal for displaying the preview */}
      <FilePreviewModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        previewImageUrl={previewImageUrl}
        fileExtension={fileExtension}
        isMobile={false} // Adjust this based on your logic
      />

      <DeleteFileModal
        isOpen={isDeleteFile}
        toggle={toggleModal}
        deletingFile={deletingFile}
        onDelete={() => {
          if (selectedFile) {
            handleDeleteFile(selectedFile, "delete");
            setIsDeleteFile(false);
          }
        }}
        fileName={selectedFile?.name || ""}
      />
    </Row>
  );
};

export default Files;
